import { SERVER_URL } from '@aca-new/app/shared/constants/app.constants';
import { ECookieKeys } from '@aca-new/app/shared/models/enums/cookie-keys.enum';
import { EStorageKeys } from '@aca-new/app/shared/models/enums/storage-keys.enum';
import { IHttpResponseBody } from '@aca-new/app/shared/models/interfaces/http-response-body.interface';
import { HttpService } from '@aca-new/app/shared/services/http-services/http.service';
import { CookieStorageService } from '@aca-new/app/shared/services/storage-services/cookie-storage/cookie-storage.service';
import { StorageService } from '@aca-new/app/shared/services/storage-services/storage.service';
import { inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

/**
 * Shared eligibility check for NPS / report CSAT (`GET /user/{userId}/show-survey`).
 */
@Injectable({
  providedIn: 'root',
})
export class SurveyVisibilityService {
  private readonly _storageService: StorageService = inject(StorageService);
  private readonly _cookieStorage: CookieStorageService = inject(CookieStorageService);
  private readonly _httpService: HttpService = inject(HttpService);

  /**
   * @returns {Observable<boolean>} Whether the survey UI may be shown for the current user.
   */
  public getIsSurveyVisible$(): Observable<boolean> {
    const userId = this._storageService.getItem(EStorageKeys.USER_ID);
    const externalReportAuthToken = this._cookieStorage.get(ECookieKeys.EXTERNAL_REPORT_AUTH_TOKEN);

    if (externalReportAuthToken) {
      return of(false);
    }

    return this._httpService.httpClient
      .get<IHttpResponseBody<boolean>>(`${SERVER_URL}/user/${userId}/show-survey`, {
        observe: 'body',
      })
      .pipe(map((response: IHttpResponseBody<boolean>): boolean => response.content || false));
  }

  /**
   * @description
   * Refreshes NPS / CSAT cooldown after the user completes an inline report rating.
   * @returns {Observable<void>} Completes when the cooldown refresh request finishes.
   */
  public refreshSurveyLastShow$(): Observable<void> {
    const userId = this._storageService.getItem(EStorageKeys.USER_ID);

    return this._httpService.httpClient
      .post<IHttpResponseBody<boolean>>(`${SERVER_URL}/user/${userId}/survey-last-show`, null, {
        observe: 'body',
      })
      .pipe(map((): void => undefined));
  }
}
